import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Search, Filter, ChevronDown, ExternalLink, Package, Globe, TrendingUp } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const OC = { care: "#10b981", utility: "#3b82f6", creative: "#a855f7", extraction: "#ef4444" };

const INDUSTRIES = [
  "Healthcare & Life Sciences", "Finance & Insurance", "Developer Tools & Infrastructure",
  "Creative & Media", "Education & Research", "General Productivity",
  "Retail & E-commerce", "Security & Defense", "Manufacturing & Industrial",
  "Transportation & Logistics", "Agriculture & Environment", "Legal & Compliance",
  "Social Impact & Nonprofit", "Communication & Telecom", "Real Estate & Construction",
  "Entertainment & Gaming",
];

const Shimmer = ({ className }) => (
  <div className={`animate-pulse bg-white/[0.06] rounded ${className}`} />
);

export const ProductExplorer = () => {
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("");
  const [filterOrientation, setFilterOrientation] = useState("");
  const [offset, setOffset] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const limit = 50;

  useEffect(() => {
    axios.get(`${API}/api/companies/solutions-stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit, offset };
      if (searchQuery) params.search = searchQuery;
      if (filterIndustry) params.industry = filterIndustry;
      if (filterOrientation) params.orientation = filterOrientation;
      const r = await axios.get(`${API}/api/companies/solutions-products`, { params });
      setProducts(r.data.products || []);
      setTotal(r.data.total || 0);
    } catch {
      setProducts([]);
    }
    setLoading(false);
  }, [searchQuery, filterIndustry, filterOrientation, offset]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchProducts();
  };

  return (
    <div className="space-y-6" data-testid="product-explorer">
      {/* Stats header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats ? (
          <>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
              <Package className="w-5 h-5 text-amber-400 mx-auto mb-2" />
              <p className="text-2xl font-black text-white">{(stats.total_products || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">AI Products</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
              <Globe className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
              <p className="text-2xl font-black text-white">{(stats.top_countries || []).length}+</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Countries</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
              <TrendingUp className="w-5 h-5 text-blue-400 mx-auto mb-2" />
              <p className="text-2xl font-black text-white">{stats.orientations?.care || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Care-Oriented</p>
            </div>
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 text-center">
              <TrendingUp className="w-5 h-5 text-violet-400 mx-auto mb-2" />
              <p className="text-2xl font-black text-white">{stats.orientations?.creative || 0}</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">Creative</p>
            </div>
          </>
        ) : (
          [1,2,3,4].map(i => <Shimmer key={i} className="h-24 rounded-xl" />)
        )}
      </div>

      {/* Search and filters */}
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search AI products... (e.g., AI Force, Topaz, Ignio)"
              className="w-full pl-10 pr-4 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/30"
              data-testid="product-search-input"
            />
          </div>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-slate-400 hover:text-white transition-colors flex items-center gap-1.5"
            data-testid="product-filter-toggle"
          >
            <Filter className="w-4 h-4" />
            <ChevronDown className={`w-3 h-3 transition-transform ${showFilters ? "rotate-180" : ""}`} />
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-amber-500/20 border border-amber-500/30 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-colors"
            data-testid="product-search-btn"
          >
            Search
          </button>
        </form>

        {showFilters && (
          <div className="mt-3 pt-3 border-t border-white/[0.06] grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Industry</label>
              <select
                value={filterIndustry}
                onChange={e => { setFilterIndustry(e.target.value); setOffset(0); }}
                className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-sm text-white focus:outline-none"
                data-testid="product-filter-industry"
              >
                <option value="">All Industries</option>
                {INDUSTRIES.map(ind => <option key={ind} value={ind}>{ind}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 block">Orientation</label>
              <select
                value={filterOrientation}
                onChange={e => { setFilterOrientation(e.target.value); setOffset(0); }}
                className="w-full px-3 py-2 bg-white/[0.03] border border-white/[0.06] rounded-lg text-sm text-white focus:outline-none"
                data-testid="product-filter-orientation"
              >
                <option value="">All Orientations</option>
                <option value="care">Care</option>
                <option value="utility">Utility</option>
                <option value="creative">Creative</option>
                <option value="extraction">Extraction</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {total.toLocaleString()} products found
          {filterIndustry && <span className="text-amber-400"> in {filterIndustry}</span>}
          {filterOrientation && <span style={{ color: OC[filterOrientation] }}> ({filterOrientation})</span>}
        </p>
        {(filterIndustry || filterOrientation || searchQuery) && (
          <button
            onClick={() => { setFilterIndustry(""); setFilterOrientation(""); setSearchQuery(""); setOffset(0); }}
            className="text-[10px] text-slate-500 hover:text-white transition-colors underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Products table */}
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-white/[0.02] border-b border-white/[0.06] text-[10px] text-slate-500 uppercase tracking-wider font-medium">
          <div className="col-span-4">Product</div>
          <div className="col-span-3">Company</div>
          <div className="col-span-2">Country</div>
          <div className="col-span-1 text-center">CII</div>
          <div className="col-span-2 text-center">Orientation</div>
        </div>

        {loading ? (
          <div className="divide-y divide-white/[0.03]">
            {[1,2,3,4,5,6,7,8].map(i => (
              <div key={i} className="grid grid-cols-12 gap-2 px-4 py-3">
                <div className="col-span-4"><Shimmer className="h-4 w-32" /></div>
                <div className="col-span-3"><Shimmer className="h-4 w-24" /></div>
                <div className="col-span-2"><Shimmer className="h-4 w-16" /></div>
                <div className="col-span-1"><Shimmer className="h-4 w-8 mx-auto" /></div>
                <div className="col-span-2"><Shimmer className="h-4 w-14 mx-auto" /></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="divide-y divide-white/[0.03]">
            {products.map((p, i) => (
              <div key={`${p.product_name}-${p.company_name}-${i}`} className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-white/[0.02] transition-colors group">
                <div className="col-span-4 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{p.product_name}</p>
                </div>
                <div className="col-span-3 min-w-0">
                  <p className="text-sm text-slate-300 truncate">{p.company_name}</p>
                  {p.domain && (
                    <a href={`https://${p.domain}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-amber-400 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {p.domain} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  )}
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-slate-400">{p.country}</p>
                </div>
                <div className="col-span-1 text-center">
                  <span className="text-sm font-bold" style={{ color: p.cii_score >= 30 ? "#10b981" : p.cii_score >= 15 ? "#f59e0b" : "#ef4444" }}>
                    {p.cii_score}
                  </span>
                </div>
                <div className="col-span-2 text-center">
                  <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{
                    color: OC[p.product_orientation || p.value_orientation],
                    borderColor: `${OC[p.product_orientation || p.value_orientation]}33`,
                    background: `${OC[p.product_orientation || p.value_orientation]}11`,
                  }}>
                    {p.product_orientation || p.value_orientation}
                  </span>
                </div>
              </div>
            ))}
            {products.length === 0 && !loading && (
              <p className="text-center text-sm text-slate-500 py-12">No products found matching your criteria</p>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            className="px-4 py-2 text-xs text-slate-400 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="product-prev-page"
          >
            Previous
          </button>
          <span className="text-xs text-slate-500">
            {offset + 1}-{Math.min(offset + limit, total)} of {total.toLocaleString()}
          </span>
          <button
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            className="px-4 py-2 text-xs text-slate-400 bg-white/[0.03] border border-white/[0.06] rounded-lg hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            data-testid="product-next-page"
          >
            Next
          </button>
        </div>
      )}

      {/* Top products by CII */}
      {stats?.top_products?.length > 0 && (
        <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-bold text-white mb-4">Highest CII Products</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {stats.top_products.slice(0, 10).map((p, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 bg-white/[0.02] rounded-lg">
                <span className="text-[10px] font-bold text-slate-600 w-4">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate">{p.product_name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{p.company_name} &middot; {p.country}</p>
                </div>
                <span className="text-sm font-black" style={{ color: OC[p.value_orientation] }}>{p.cii_score}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductExplorer;
